import { Navigation } from "@/components/Navigation"
import { Footer } from "@/components/Footer"
import { SEO } from "@/components/SEO"
import { PageFAQ } from "@/components/PageFAQ"
import { technologiesFAQs } from "@/data/faqData"

const techCategories = [
    {
        num: "01",
        title: "AI & Intelligent Automation",
        description: "Mastering the cognitive layer to orchestrate agentic workflows, extensive language models, and predictive decisioning at enterprise scale.",
        items: [
            "Agentic AI",
            "Generative AI (LLMs)",
            "Natural Language Processing",
            "Machine Learning",
            "Cognitive APIs",
            "UiPath, Automation Anywhere",
        ],
    },
    {
        num: "02",
        title: "Enterprise Platforms",
        description: "Deep architectural expertise across the mission-critical core systems that drive global business operations and data flow.",
        items: [
            "Sitecore",
            "Salesforce",
            "SAP",
            "Oracle Suite",
            "Workday",
            "SAP S/4HANA & ECC",
        ],
        logos: [
            { name: "Sitecore", src: "/sitecore.png" },
            { name: "Salesforce", src: "/salesforce.png" },
        ],
    },
    {
        num: "03",
        title: "Cloud & Infrastructure",
        description: "Resilient, cloud-native foundations engineered for infinite scale, multi-cloud flexibility, and zero-trust security.",
        items: [
            "AWS",
            "Microsoft Azure",
            "Google Cloud",
            "Kubernetes",
            "DevOps Toolchains",
        ],
    },
    {
        num: "04",
        title: "Data & Analytics",
        description: "Transforming raw information into a unified intelligence fabric that powers real-time strategic foresight and action.",
        items: [
            "Snowflake",
            "Databricks",
            "Tableau",
            "Power BI",
            "BigQuery",
        ],
        logos: [
            { name: "Snowflake", src: "/snowflake.png" },
            { name: "Tableau", src: "/tableau.png" },
        ],
    },
    {
        num: "05",
        title: "Integration & APIs",
        description: "The connective tissue ensuring seamless, secure interoperability across your entire disparate digital estate.",
        items: ["MuleSoft", "Boomi", "API-first Integration Frameworks", "Dell Boomi"],
    },
    {
        num: "06",
        title: "Quality Engineering & Testing",
        description: "Rigorous, automated validation standards ensuring mission-critical reliability and flawless user experiences.",
        items: [
            "Selenium",
            "Cypress",
            "React, Angular, Node.js, .NET",
            "Java, Python",
        ],
    },
    {
        num: "07",
        title: "Application Engineering",
        description: "Crafting high-performance, future-proof applications on modern, scalable stacks designed for longevity.",
        items: ["React", "Angular", "Node.js", ".NET", "Java", "Python"],
    },
]

const Technologies = () => {
    return (
        <div className="min-h-screen bg-white">
            <SEO
                title="Our Technology Ecosystem | RSA Tech Group"
                description="The foundational platforms RSA Tech Group builds upon to architect, integrate, and scale intelligent enterprises. Strategic Engineering Partnerships with the Industry's Best."
                image="/tech-hero-bg.png"
            />
            <Navigation />

            {/* Hero Section */}
            <div className="relative pt-48 pb-24 overflow-hidden bg-[#0f1629]">
                <div
                    className="absolute inset-0 z-0 bg-cover bg-center bg-no-repeat"
                    style={{ backgroundImage: "url('/tech-hero-bg.png')" }}
                >
                    <div className="absolute inset-0 bg-[#0f1629]/70 backdrop-blur-[2px]" />
                </div>
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_80%_50%_at_50%_30%,rgba(28,43,110,0.2),transparent_60%)]" />
                <div className="absolute bottom-0 right-0 w-[400px] h-[400px] bg-[#FDA400]/[0.04] rounded-full blur-[120px]" />

                <div className="section-container relative z-10 text-center">
                    <div className="max-w-4xl mx-auto">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-6">
                            Technology Stack
                        </p>
                        <h1 className="text-4xl md:text-6xl lg:text-7xl font-black text-white mb-6 leading-[1.1] tracking-tight">
                            Our Technology{" "}
                            <span className="text-[#FDA400]">Ecosystem</span>
                        </h1>
                        <p className="text-xl md:text-2xl text-slate-300 max-w-3xl mx-auto mb-10 font-light leading-relaxed">
                            The foundational platforms RSA Tech Group builds upon to architect, integrate, and scale intelligent enterprises.
                        </p>
                        <p className="text-xs text-slate-500 uppercase tracking-[0.25em] font-semibold">
                            Strategic Engineering Partnerships with the Industry's Best
                        </p>
                    </div>
                </div>
            </div>

            {/* Tech Grid */}
            <section className="py-24 bg-gradient-to-b from-slate-50 via-white to-slate-50 relative overflow-hidden">
                {/* Dot grid bg */}
                <div
                    className="absolute inset-0 opacity-[0.25]"
                    style={{
                        backgroundImage: "radial-gradient(circle, #1C2B6E 0.5px, transparent 0.5px)",
                        backgroundSize: "28px 28px",
                    }}
                />
                <div className="absolute top-0 left-1/3 w-[500px] h-[500px] bg-[#1C2B6E]/[0.02] rounded-full blur-[150px]" />
                <div className="absolute bottom-0 right-1/4 w-[400px] h-[400px] bg-[#FDA400]/[0.02] rounded-full blur-[120px]" />

                <div className="section-container relative z-10">
                    {/* Section header */}
                    <div className="text-center mb-20">
                        <p className="text-[#FDA400] text-sm font-semibold uppercase tracking-[0.2em] mb-4">
                            What We Work With
                        </p>
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-black text-[#1C2B6E] tracking-tight leading-[1.1]">
                            7 Pillars of{" "}
                            <span className="text-[#FDA400]">Engineering</span>
                        </h2>
                    </div>

                    {/* Bento grid: first row 3 cols, second row 2+2, third row 3 cols */}
                    <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-7xl mx-auto">
                        {techCategories.map((category, index) => (
                            <div
                                key={index}
                                className={`partner-card group rounded-3xl overflow-hidden shadow-[0_2px_8px_rgba(0,0,0,0.04)] hover:shadow-[0_25px_80px_rgba(28,43,110,0.12)] transition-all duration-500 hover:-translate-y-2 ${
                                    index === 0 ? "lg:col-span-2" : ""
                                }`}
                            >
                                {/* Shine sweep */}
                                <div className="absolute inset-0 z-20 overflow-hidden rounded-3xl pointer-events-none">
                                    <div className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent opacity-0 group-hover:opacity-100 group-hover:animate-[shine_0.8s_ease-in-out]" />
                                </div>

                                {/* Top accent */}
                                <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-[#FDA400] to-[#f5b800] scale-x-0 group-hover:scale-x-100 transition-transform duration-500 origin-left z-10" />

                                {/* Glow */}
                                <div className="absolute -bottom-12 -right-12 w-44 h-44 bg-[#FDA400]/[0.04] rounded-full blur-[50px] group-hover:bg-[#FDA400]/10 group-hover:scale-110 transition-all duration-700" />

                                {/* Ghost number */}
                                <span className="absolute top-4 right-6 text-8xl font-black text-[#1C2B6E]/[0.03] leading-none select-none group-hover:text-[#1C2B6E]/[0.07] transition-colors duration-500">
                                    {category.num}
                                </span>

                                <div className="p-8 md:p-10 h-full flex flex-col relative z-10">
                                    {/* Number label */}
                                    <div className="text-xs font-bold text-[#FDA400] uppercase tracking-[0.2em] mb-5">
                                        {category.num}
                                    </div>

                                    <h3 className="text-2xl md:text-3xl font-black text-[#1C2B6E] tracking-tight mb-3">
                                        {category.title}
                                    </h3>

                                    <p className="text-slate-500 text-sm leading-relaxed font-light mb-6">
                                        {category.description}
                                    </p>

                                    {/* Partner logos if available */}
                                    {category.logos && (
                                        <div className="flex items-center gap-5 mb-6">
                                            {category.logos.map((logo, idx) => (
                                                <div key={idx} className="relative">
                                                    <div className="absolute inset-0 bg-[#FDA400]/[0.06] blur-[15px] rounded-full scale-150 opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                                                    <img
                                                        src={logo.src}
                                                        alt={logo.name}
                                                        className="h-7 w-auto object-contain relative z-10 opacity-70 group-hover:opacity-100 transition-opacity duration-300"
                                                    />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Tech tags */}
                                    <div className="flex flex-wrap gap-2 mt-auto pt-6 border-t border-slate-100">
                                        {category.items.map((item, i) => (
                                            <span
                                                key={i}
                                                className="px-3 py-1.5 bg-[#1C2B6E]/[0.04] text-[#1C2B6E]/70 text-xs font-medium rounded-full border border-[#1C2B6E]/[0.06] hover:bg-[#FDA400]/10 hover:border-[#FDA400]/30 hover:text-[#FDA400] transition-all duration-300 cursor-default"
                                            >
                                                {item}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            <PageFAQ
                faqs={technologiesFAQs}
                title="Technology Stack FAQs"
                subtitle="Common questions about our technology expertise and platforms"
                variant="muted"
            />

            <Footer />
        </div>
    )
}

export default Technologies
